import { useState, useEffect, useCallback, useRef } from 'react';
import { TimerConfig, getTotalTimerSeconds, formatTimeRemaining } from '../utils/timerUtils';

interface UseTimerProgressProps {
  timerConfig: TimerConfig;
  isActive: boolean;
  startTime?: number;
  /** Estado on/off del dispositivo: elige duración auto-off vs auto-on */
  deviceIsOn?: boolean;
}

interface TimerProgressResult {
  timeRemaining: number;
  progress: number;
  isRunning: boolean;
  formattedTime: string;
  /** true solo si el countdown llegó a 0 por tiempo; false si se canceló/desactivó */
  completedNaturally: boolean;
}

/**
 * Hook personalizado para manejar el progreso del timer
 */
export const useTimerProgress = ({ 
  timerConfig, 
  isActive, 
  startTime,
  deviceIsOn
}: UseTimerProgressProps): TimerProgressResult => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [completedNaturally, setCompletedNaturally] = useState<boolean>(false);
  const completedRef = useRef(false);

  const totalSeconds = getTotalTimerSeconds(timerConfig, deviceIsOn);

  const formatTime = useCallback((seconds: number): string => {
    return formatTimeRemaining(seconds);
  }, []);

  const progress = totalSeconds > 0 ? Math.max(0, 1 - (timeRemaining / totalSeconds)) : 0;

  useEffect(() => {
    if (!isActive || !timerConfig.enabled || totalSeconds <= 0 || !startTime) {
      // Cancelación / desactivación: no es fin natural del countdown
      completedRef.current = false;
      setCompletedNaturally(false);
      setTimeRemaining(0);
      setIsRunning(false);
      return;
    }

    completedRef.current = false;
    setCompletedNaturally(false);

    const calculateRemaining = () => {
      const now = Date.now();
      const elapsed = (now - startTime) / 1000;
      return Math.max(0, totalSeconds - elapsed);
    };

    const initialRemaining = calculateRemaining();
    // Timer ya vencido al activar (p.ej. estado viejo en localStorage): no forzar UI
    if (initialRemaining <= 0) {
      setTimeRemaining(0);
      setIsRunning(false);
      return;
    }

    setIsRunning(true);

    const markCompletedIfNeeded = (remaining: number) => {
      if (remaining <= 0 && !completedRef.current) {
        completedRef.current = true;
        setCompletedNaturally(true);
        setIsRunning(false);
      }
      return remaining;
    };

    setTimeRemaining(initialRemaining);

    const interval = setInterval(() => {
      const remaining = markCompletedIfNeeded(calculateRemaining());
      setTimeRemaining(remaining);
      
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      // Cleanup por cambio de deps / unmount: no marcar como completado
      setIsRunning(false);
    };
  }, [isActive, timerConfig.enabled, totalSeconds, startTime]);

  return {
    timeRemaining,
    progress,
    isRunning,
    formattedTime: formatTime(timeRemaining),
    completedNaturally
  };
};
